import { ArrowLeft, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { ConfirmDialog } from "@/client/components/confirm-dialog";
import { PageHeader } from "@/client/components/page-header";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import { Card, CardContent } from "@/client/components/ui/card";
import { Skeleton } from "@/client/components/ui/skeleton";
import { capacityRequestsAdminApi } from "@/client/features/admin/capacity-requests-admin-api";
import type { CapacityRequestView } from "@/client/features/capacity-request/capacity-request-api";
import { CapacitySummary } from "@/client/features/capacity-request/capacity-summary";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { ApiError } from "@/client/lib/api";

function formatDateTime(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

export function StaffInquiryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isStaffAdmin = useAuthStore((s) => s.user?.staffRole === "admin");
  const [request, setRequest] = useState<CapacityRequestView | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!request) return;
    await capacityRequestsAdminApi.remove(request.id);
    toast.success("Inquiry deleted.");
    navigate("/admin/inquiries");
  };

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setRequest(null);
    setError(null);
    capacityRequestsAdminApi
      .get(id)
      .then((res) => {
        if (!cancelled) setRequest(res.request);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof ApiError ? err.message : "Failed to load inquiry.";
        setError(message);
        toast.error(message);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/admin/inquiries">
            <ArrowLeft className="size-4" />
            All inquiries
          </Link>
        </Button>
        <PageHeader
          title={request?.company || "Inquiry"}
          description={request ? request.email : "Capacity request detail."}
        >
          {request ? (
            <div className="flex items-center gap-3">
              <Badge variant={request.status === "submitted" ? "secondary" : "outline"}>
                {request.status}
              </Badge>
              {isStaffAdmin ? (
                <ConfirmDialog
                  trigger={
                    <Button variant="destructive" size="sm">
                      <Trash2 className="size-4" />
                      Delete inquiry
                    </Button>
                  }
                  title="Delete inquiry?"
                  description={
                    <>
                      This permanently deletes the inquiry from{" "}
                      <span className="text-foreground font-medium">
                        {request.company || "an unnamed company"}
                      </span>
                      {request.contactName ? ` (${request.contactName})` : ""} ·{" "}
                      <span className="text-foreground font-medium">{request.email}</span>. It also
                      clears the magic-link tokens tied to this email, so the same address can start
                      a fresh capacity request. This cannot be undone.
                    </>
                  }
                  confirmLabel="Delete inquiry"
                  pendingLabel="Deleting…"
                  errorMessage="Couldn't delete this inquiry."
                  onConfirm={handleDelete}
                />
              ) : null}
            </div>
          ) : null}
        </PageHeader>
      </div>

      {error && request === null ? (
        <p className="text-muted-foreground text-sm">Couldn't load this inquiry.</p>
      ) : request === null ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <>
          <Card>
            <CardContent className="text-muted-foreground grid gap-1 py-4 text-sm sm:grid-cols-2">
              <div>
                Submitted:{" "}
                <span className="text-foreground">{formatDateTime(request.submittedAt)}</span>
              </div>
              <div>
                Last updated:{" "}
                <span className="text-foreground">{formatDateTime(request.updatedAt)}</span>
              </div>
              {request.slackNotificationError ? (
                <div className="text-destructive sm:col-span-2">
                  GTM notification error: {request.slackNotificationError}
                </div>
              ) : null}
            </CardContent>
          </Card>

          {/* Render answers exactly as submitted (no draft normalization): these
              are historical, immutable, and the summary already falls back to the
              raw id when an option's label has since been retired. */}
          <CapacitySummary
            data={{
              company: request.company ?? "",
              contactName: request.contactName ?? "",
              role: request.role ?? "",
              answers: request.answers,
            }}
            email={request.email}
          />
        </>
      )}
    </div>
  );
}
